/**
 * Color utility functions to handle both Tailwind classes and hex colors
 */
export function isHexColor(color) {
    return /^#[0-9a-fA-F]{6}$/.test(color);
}
export function getColorStyle(color, property) {
    if (isHexColor(color)) {
        return { [property]: color };
    }
    return {};
}
export function getColorClassName(color, prefix) {
    if (isHexColor(color)) {
        return '';
    }
    return `${prefix}-${color}`;
}
export function applyColor(color, type) {
    const prefixMap = {
        background: 'bg',
        text: 'text',
        border: 'border'
    };
    const propertyMap = {
        background: 'backgroundColor',
        text: 'color',
        border: 'borderColor'
    };
    return {
        className: getColorClassName(color, prefixMap[type]),
        style: getColorStyle(color, propertyMap[type])
    };
}
/**
 * Converts HSL values to hex string
 */
export function hslToHex(h, s, l) {
    const sNorm = s / 100;
    const lNorm = l / 100;
    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;
    let r, g, b;
    if (0 <= h && h < 60) {
        [r, g, b] = [c, x, 0];
    }
    else if (60 <= h && h < 120) {
        [r, g, b] = [x, c, 0];
    }
    else if (120 <= h && h < 180) {
        [r, g, b] = [0, c, x];
    }
    else if (180 <= h && h < 240) {
        [r, g, b] = [0, x, c];
    }
    else if (240 <= h && h < 300) {
        [r, g, b] = [x, 0, c];
    }
    else {
        [r, g, b] = [c, 0, x];
    }
    const toHex = (n) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
/**
 * Converts hex to HSL values
 */
export function hexToHsl(hex) {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.slice(0, 2), 16) / 255;
    const g = parseInt(cleanHex.slice(2, 4), 16) / 255;
    const b = parseInt(cleanHex.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) {
        return { h: 0, s: 0, l: l * 100 };
    }
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    switch (max) {
        case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            break;
        case g:
            h = ((b - r) / d + 2) / 6;
            break;
        case b:
            h = ((r - g) / d + 4) / 6;
            break;
        default:
            h = 0;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
}
/**
 * Get contrast ratio between two colors
 */
export function getContrastRatio(color1, color2) {
    const getLuminance = (color) => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16) / 255;
        const g = parseInt(hex.slice(2, 4), 16) / 255;
        const b = parseInt(hex.slice(4, 6), 16) / 255;
        const sRGBToLinear = (c) => {
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        return 0.2126 * sRGBToLinear(r) + 0.7152 * sRGBToLinear(g) + 0.0722 * sRGBToLinear(b);
    };
    const l1 = getLuminance(color1);
    const l2 = getLuminance(color2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}
/**
 * Check if two colors have sufficient contrast for accessibility
 */
export function hasGoodContrast(color1, color2, level = 'AA') {
    if (!isHexColor(color1) || !isHexColor(color2)) {
        // For Tailwind colors, assume they have good contrast if they're different
        return color1 !== color2;
    }
    const ratio = getContrastRatio(color1, color2);
    return level === 'AAA' ? ratio >= 7 : ratio >= 4.5;
}
